import { useState, useCallback, useEffect, useMemo, useRef, useContext } from 'react';
import useLocalStorageWithExpiry, {
  getLocalStorageKey,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useLocalStorageWithExpiry';
import _ from 'lodash';
import {
  useChatStoreRef,
  useMostRecentResponseFullObject,
  useStepParams,
  useStepResponse,
  useMostRecentResponseForStepId,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import {
  InteractionStepType,
  ModelResponseEditorStepResponse,
  UIGenerationMetadata,
} from '@scale/shared/types/chat';
import { getOriginalResponseAttachments, getOriginalResponseMessageMetadata } from '../utils';
import { ChatTaskContext } from '../../../contexts/ChatTaskContext';
export enum ToolBoxTab {
  DiffView = 'Diff View',
  MarkdownPreview = 'Markdown Preview',
}

export type UseModelResponseEditor = ReturnType<typeof useModelResponseEditor>;

type EditorType = 'milkdown' | 'plain-text';

export const modifyResponseBasedOnRegex = (text: string, terminationRegex?: RegExp) => {
  if (terminationRegex && text) {
    const match = terminationRegex.exec(text);
    if (match) {
      return text.slice(0, match.index);
    }
  }
  return text;
};

const useModelResponseEditor = (responseIndex: number) => {
  const taskContext = useContext(ChatTaskContext);
  const chatStoreRef = useChatStoreRef();
  const response = useStepResponse<ModelResponseEditorStepResponse>(responseIndex);
  const stepParams = useStepParams<InteractionStepType.ModelResponseEditor>(responseIndex);

  const [reactUIGeneratedUrl, setReactUIGeneratedUrl] = useState<string | null>(
    response?.context?.reactUIGeneratedUrl ?? null,
  );

  const [reactUIGeneratedUrlWithMetadata, setReactUIGeneratedUrlWithMetadata] = useState<
    {
      reactUIGeneratedUrl?: string;
    } & UIGenerationMetadata
  >({
    reactUIGeneratedUrl: response?.context?.reactUIGeneratedUrl,
    lastUIGenerationTimestamp: response?.context?.lastUIGenerationTimestamp,
    lastRunLanguage: response?.context?.lastRunLanguage,
    lastRunResult: response?.context?.lastRunResult,
    lastErrorMessage: response?.context?.lastErrorMessage,
    lastCodeRunHash: response?.context?.lastCodeRunHash,
  });

  const terminationRegex = useMemo(
    () =>
      stepParams.params.termination_regex_condition
        ? new RegExp(stepParams.params.termination_regex_condition, 's')
        : undefined,
    [stepParams.params.termination_regex_condition],
  );

  const localSaveKey = getLocalStorageKey({
    assignmentId: taskContext.assignmentId,
    stepType: stepParams.type,
    responseIndex,
    disabled: taskContext.isPipelineV2,
  });

  const lastResponseObject = useMostRecentResponseFullObject(responseIndex);
  const mostRecentResponseForStepId = useMostRecentResponseForStepId(
    stepParams.params.step_id_to_use_output_as_original_text,
    responseIndex,
  );
  const mostRecentResponseBase = lastResponseObject?.output ?? '';

  const mostRecentResponse = useMemo(() => {
    if (
      stepParams.params.step_id_to_use_output_as_original_text &&
      mostRecentResponseForStepId?.output.length
    ) {
      return mostRecentResponseForStepId.output;
    }
    return mostRecentResponseBase;
  }, [
    stepParams.params.step_id_to_use_output_as_original_text,
    mostRecentResponseForStepId,
    mostRecentResponseBase,
  ]);

  const originalText = useMemo(
    () => modifyResponseBasedOnRegex(mostRecentResponse, terminationRegex),
    [mostRecentResponse, terminationRegex],
  );
  const baseResponse = useMemo(
    () => modifyResponseBasedOnRegex(response ? response.output : originalText, terminationRegex),
    [response, originalText, terminationRegex],
  );

  const originalResponseAttachments = getOriginalResponseAttachments(response, lastResponseObject);
  const originalResponseMessageMetadata = getOriginalResponseMessageMetadata(
    response,
    lastResponseObject,
  );

  const [editorType, setEditorType] = useState<EditorType>('milkdown');
  const [text, setText, clearBackup] = useLocalStorageWithExpiry(baseResponse, localSaveKey);
  const backupInitiallyLoadedRef = useRef<boolean>(false);
  const [resAttachments, setResAttachments] = useState<any>(originalResponseAttachments);
  const milkdownRef = useRef<{ update: (text: string) => void }>(null);

  const [activeToolBoxTab, setActiveToolBoxTab] = useState<string>(ToolBoxTab.DiffView);
  const [isToolBoxOpen, setIsToolBoxOpen] = useState<boolean>(false);

  const handlePassUpdateToMilkdown = useCallback((text: string) => {
    const { current } = milkdownRef;
    if (!current) return;
    if (terminationRegex) {
      current.update(modifyResponseBasedOnRegex(text, terminationRegex));
    } else {
      current.update(text);
    }
    // eslint-disable-next-line
  }, []);

  /*
   * Mark the step as incomplete if the text is not equal to the base response and the step is marked as complete
   * This ensures that if there is a local storage save, the step is correctly marked as incomplete
   */
  useEffect(() => {
    const store = chatStoreRef.current;
    const state = store.getState();
    const markStepAsIncompletedByIndex = state.markStepAsIncompletedByIndex;
    const response = state.responses[responseIndex];
    const completed = response ? state.completedSteps[responseIndex] : false;
    if (completed && !_.isEqual(text, baseResponse)) {
      markStepAsIncompletedByIndex(responseIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only run on mount

  // NOTE: When originalText changes due to response propagation, we need to update the text in the editor
  useEffect(() => {
    // If the backup has not been loaded at least once yet, don't update the text
    if (!backupInitiallyLoadedRef.current) {
      backupInitiallyLoadedRef.current = true;
      return;
    }

    // If there is no non-empty original text, don't update the text
    if (!originalText) {
      return;
    }

    setText(originalText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalText]); // setText depends on initialValue, which changes upon submission, but we don't need to re-run this effect when that happens

  return {
    baseResponse,
    originalText,
    text,
    setText,
    clearBackup,
    milkdownRef,
    activeToolBoxTab,
    setActiveToolBoxTab,
    isToolBoxOpen,
    setIsToolBoxOpen,
    handlePassUpdateToMilkdown,
    editorType,
    setEditorType,
    resAttachments,
    setResAttachments,
    reactUIGeneratedUrl,
    setReactUIGeneratedUrl,
    reactUIGeneratedUrlWithMetadata,
    setReactUIGeneratedUrlWithMetadata,
    lastResponseObject,
    originalResponseMessageMetadata,
  };
};

export default useModelResponseEditor;
